import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import axios from 'axios';
import { makeStyles } from '@mui/styles';
import ParkingImage from '../assets/nimbleparknewlogo.svg';
import { useSnackbar } from './SnackbarContext';
import { Email, Phone, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import config from '../config';
import { persistentStorage } from '../utils/storage';
import { markSessionStart } from '../utils/session';

const useStyles = makeStyles(() => ({
  rootContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    padding: '16px',
    overflow: 'hidden',
    fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
  },
  loginCard: {
    width: '100%',
    height: '100%',
    position: 'relative',
    background: 'white',
    overflow: 'hidden',
    borderRadius: 30,
    minHeight: 750,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    '@media (max-width: 768px)': {
      borderRadius: 0,
      boxShadow: 'none',
      minHeight: '100vh',
    },
  },
  topBack: {
    position: 'absolute',
    top: 16,
    left: 16,
    color: '#C084FC',
  },
  logo: {
    width: 126,
    height: 114,
    left: '50%',
    top: 40,
    position: 'absolute',
    transform: 'translateX(-50%)',
    '@media (max-width: 768px)': {
      top: 30,
      width: 100,
      height: 90,
    },
  },
  tagline: {
    left: '50%',
    top: 150,
    position: 'absolute',
    color: '#C084FC',
    fontSize: 18,
    fontFamily: 'Sansation',
    fontWeight: '700',
    lineHeight: '19.10px',
    whiteSpace: 'nowrap',
    transform: 'translateX(-50%)',
    textAlign: 'center',
    '@media (max-width: 768px)': {
      top: 125,
      fontSize: 16,
    },
  },
  formContainer: {
    position: 'absolute',
    top: 240,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    padding: '0 20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      top: 220,
    },
  },
  phoneInput: {
    width: '100%',
    height: 51,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1.04px #CCCCCC solid',
      '& fieldset': { border: '1.04px #CCCCCC solid' },
      '&:hover fieldset': { border: '1.04px #CCCCCC solid' },
      '&.Mui-focused fieldset': { border: '1.04px #CCCCCC solid' },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 16,
      fontFamily: 'Nunito',
    },
  },
  passwordInput: {
    width: '100%',
    height: 51,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1.04px #CCCCCC solid',
      '& fieldset': { border: '1.04px #CCCCCC solid' },
      '&:hover fieldset': { border: '1.04px #CCCCCC solid' },
      '&.Mui-focused fieldset': { border: '1.04px #CCCCCC solid' },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 16,
      fontFamily: 'Nunito',
    },
  },
  signInButton: {
    width: '100%',
    height: 51,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.4,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Nunito',
    fontWeight: '700',
    lineHeight: '33.19px',
    cursor: 'pointer',
    '&:hover': { background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)' },
    '&:disabled': { background: '#E0E0E0', color: '#9E9E9E', cursor: 'not-allowed' },
  },
  linksContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '0 20px',
    boxSizing: 'border-box',
    marginTop: '24px',
  },
  signUpText: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
    lineHeight: '1.5',
  },
  forgotPassword: {
    color: '#C084FC',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  customerLogin: {
    textAlign: 'center',
    color: '#C084FC',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    textDecoration: 'none',
    marginTop: '8px',
    '&:hover': { textDecoration: 'underline' },
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 12,
    fontFamily: 'Nunito',
  },
  loadingSpinner: { color: 'white' },
  otpRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginTop: 28,
    marginBottom: 18,
  },
  otpBox: {
    width: 56,
    height: 56,
    borderRadius: 14,
    border: '1.6px #DDDDDD solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#757575',
    outline: 'none',
    background: '#fff',
    textAlign: 'center',
  },
  otpBoxActive: { border: '1.8px #C084FC solid' },
  resendOtp: {
    color: '#009DFF',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'left',
    marginTop: 8,
  },
}));

export default function AssociatesLogin({ setRole }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [step, setStep] = useState('identifier'); // identifier | otp | setPassword | password | alert
  const [identifier, setIdentifier] = useState('');
  /** associate | agency | valet_service — B2B accounts are not User rows */
  const [accountKind, setAccountKind] = useState('associate');
  const [purpose, setPurpose] = useState('associate_login'); // associate_login | associate_forgot_password

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // OTP
  const [otp, setOtp] = useState(['', '', '', '']);
  const otpRefs = useRef([]);
  const otpValue = useMemo(() => otp.join(''), [otp]);
  const otpValid = otp.every((d) => d !== '');

  // Password
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const isEmail = (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(input || '').trim());
  const isPhone = (input) => String(input || '').replace(/\D/g, '').length === 10;

  const resetOtp = () => {
    setOtp(['', '', '', '']);
    otpRefs.current?.[0]?.focus?.();
  };

  const goBack = () => {
    setError('');
    if (step === 'identifier') {
      navigate('/login');
      return;
    }
    setStep('identifier');
    setAccountKind('associate');
    resetOtp();
    setPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPurpose('associate_login');
  };

  const loginAgencySession = async (emailStr, pwd) => {
    const response = await axios.post(
      `${config.API_BASE_URL}/agency/login`,
      { email: emailStr.trim(), password: pwd }
    );
    const agency = response.data.agency;
    if (!agency) throw new Error('Invalid login response');
    await persistentStorage.set('currentAgencyId', agency.agency_id);
    await persistentStorage.set('currentAgencyName', agency.name);
    await persistentStorage.set('currentRole', 'agency');
    await persistentStorage.set('currentAgency', JSON.stringify(agency));
    await markSessionStart();
    showSnackbar('Login successful!', 'success');
    navigate('/event-organizer/dashboard');
  };

  const loginValetServiceSession = async (emailStr, pwd) => {
    const response = await axios.post(
      `${config.API_BASE_URL}/valet-service/login`,
      { email: emailStr.trim(), password: pwd }
    );
    const valetService = response.data.valet_service;
    if (!valetService) throw new Error('Invalid login response');
    await persistentStorage.set('currentValetServiceId', valetService.valet_service_id);
    await persistentStorage.set('currentValetServiceName', valetService.name);
    await persistentStorage.set('currentRole', 'valet_service');
    await persistentStorage.set('currentValetService', JSON.stringify(valetService));
    await markSessionStart();
    showSnackbar('Login successful!', 'success');
    navigate('/valet-service/dashboard');
  };

  const storeSessionAndNavigate = async ({ user, selectedHotel }) => {
    // Pass the chosen workspace's IDs so the backend broadcasts presence to
    // ONLY that workspace's room. For a pool valet attached to both an event
    // AND a property, the User row carries both event_id and hotel_id, so
    // omitting them here would fan the broadcast out to BOTH rooms (event +
    // property), making the user appear on-duty in dashboards they didn't
    // actually log into. Sending event_id explicitly (including null when a
    // property workspace was picked) blocks that leak.
    await axios.post(`${config.API_BASE_URL}/user-status/login`, {
      user_id: selectedHotel.user_id,
      hotel_id: selectedHotel.hotel_id || null,
      event_id: selectedHotel.event_id || null,
      valet_service_id: selectedHotel.valet_service_id || null,
    });

    const currentUser = {
      user_id: selectedHotel.user_id,
      name: user.name,
      phone: user.phone,
      email: user.email || null,
      role: selectedHotel.role,
      hotel_id: selectedHotel.hotel_id,
      event_id: selectedHotel.event_id || null,
    };

    await persistentStorage.set('currentUserName', user.name);
    await persistentStorage.set('currentUserNickname', user.designation || null);
    await persistentStorage.set('currentRole', selectedHotel.role);
    await persistentStorage.set('currentUserEmail', user.email || null);
    await persistentStorage.set('hotel_id', selectedHotel.hotel_id);
    await persistentStorage.set('currentEventId', selectedHotel.event_id || null);
    await persistentStorage.set('currentValetServiceId', selectedHotel.valet_service_id || null);
    await persistentStorage.set('currentValetServiceName', selectedHotel.valet_service_name || null);
    await persistentStorage.set('currentUserPhone', user.phone);
    await persistentStorage.set('currentUserId', selectedHotel.user_id);
    await persistentStorage.set('currentUser', JSON.stringify(currentUser));

    // Set a global axios header so backend authorization helpers can identify
    // the acting user without a full session/JWT system.
    if (selectedHotel.user_id) {
      axios.defaults.headers.common['X-User-Id'] = String(selectedHotel.user_id);
    }

    localStorage.setItem('currentUserName', user.name);
    if (user.designation) localStorage.setItem('currentUserNickname', user.designation);
    else localStorage.removeItem('currentUserNickname');
    localStorage.setItem('currentRole', selectedHotel.role);
    localStorage.setItem('currentUserEmail', user.email || null);
    // Guard the null write: localStorage.setItem('hotel_id', null) coerces to
    // the literal string "null", which then reads back as truthy and routes
    // pool valets (hotel_id=null from the synthetic org-workspace) into
    // /valet-dashboard with hotel_id=null in every downstream API call.
    if (selectedHotel.hotel_id != null) localStorage.setItem('hotel_id', selectedHotel.hotel_id);
    else localStorage.removeItem('hotel_id');
    if (selectedHotel.event_id) localStorage.setItem('currentEventId', selectedHotel.event_id);
    else localStorage.removeItem('currentEventId');
    if (selectedHotel.valet_service_id) localStorage.setItem('currentValetServiceId', selectedHotel.valet_service_id);
    else localStorage.removeItem('currentValetServiceId');
    if (selectedHotel.valet_service_name) localStorage.setItem('currentValetServiceName', selectedHotel.valet_service_name);
    else localStorage.removeItem('currentValetServiceName');
    localStorage.setItem('currentUserPhone', user.phone);
    localStorage.setItem('currentUserId', selectedHotel.user_id);
    localStorage.setItem('currentUser', JSON.stringify(currentUser));

    await markSessionStart();
    if (setRole) setRole(selectedHotel.role);

    // TOS always lands on the realtime, event-scoped TOS dashboard.
    // Supervisor / valet / serviceboy continue to their usual dashboards which now
    // can read currentEventId from storage.
    if (selectedHotel.role === 'time_office_supervisor') {
      return navigate('/tos-dashboard');
    }

    if (selectedHotel.role === 'manager') return navigate('/manager-dashboard');

    // Unattached VSP-pool user (synthetic org workspace from _build_hotels_payload
    // Branch 5-fallback). VSP created the employee before owning any property
    // or signing any ValetServiceAssignment, so hotel_id and event_id are both
    // null. Route by verification_status, ending at /awaiting-assignment once
    // verified — the standard dashboards assume a hotel_id and would crash.
    if (
      (selectedHotel.role === 'supervisor' || selectedHotel.role === 'valet' || selectedHotel.role === 'serviceboy')
      && !selectedHotel.hotel_id
      && !selectedHotel.event_id
      && selectedHotel.valet_service_id
    ) {
      const userData = { ...user, ...selectedHotel };
      if (selectedHotel.verification_status === 'verified') return navigate('/awaiting-assignment');
      if (selectedHotel.verification_status === 'pending') {
        return navigate('/verification-pending', { state: { userData, role: selectedHotel.role } });
      }
      return navigate('/personal-details', { state: { userData, role: selectedHotel.role } });
    }

    // Supervisor / valet routing keys off verification_status (reliable since
    // /update-personal-details flips it to 'pending'; manager send-back flips
    // it to 'unverified'). The legacy first_name-set heuristic was unreliable
    // — admin-add paths intentionally leave first_name NULL.
    //
    //   verified                                   → role dashboard
    //   pending                                    → /verification-pending (waiting room)
    //   unverified + license_number + issue        → /personal-details with rejection banner (resubmit)
    //   anything else                              → /personal-details first-time flow
    if (selectedHotel.role === 'supervisor' || selectedHotel.role === 'valet') {
      const dest = selectedHotel.role === 'supervisor' ? '/supervisor-dashboard' : '/valet-dashboard';
      const userData = { ...user, ...selectedHotel };
      if (selectedHotel.verification_status === 'verified') return navigate(dest);
      if (selectedHotel.verification_status === 'pending') {
        return navigate('/verification-pending', { state: { userData, role: selectedHotel.role } });
      }
      return navigate('/personal-details', { state: { userData, role: selectedHotel.role } });
    }
    return navigate('/dashboard');
  };

  const handlePasswordLoginResponse = async (resp) => {
    const user = resp.data?.user;
    if (!user) throw new Error('Invalid server response');

    const ASSOCIATE_ROLES = ['manager', 'supervisor', 'valet', 'serviceboy', 'time_office_supervisor'];
    const allHotels = user.hotels || [];
    const assocHotels = allHotels.filter((h) => ASSOCIATE_ROLES.includes(h.role));
    if (assocHotels.length === 0) {
      // Distinguish "wrong account type" from "associate exists but has no workspaces yet".
      // The latter happens for VSP-main-page TOS / VSP-pool valet whose VSP hasn't
      // been assigned any properties or events yet.
      if (allHotels.length === 0) {
        throw new Error(
          "Your account isn't linked to any property or event yet. Ask your organisation to assign you to a property before logging in."
        );
      }
      throw new Error('No associate accounts found. Please use customer login.');
    }

    if (assocHotels.length > 1) {
      navigate('/hotel-selection', { state: { userData: user, hotels: assocHotels } });
      return;
    }

    await storeSessionAndNavigate({ user, selectedHotel: assocHotels[0] });
  };

  const handleNext = async () => {
    setError('');
    const id = identifier.trim();
    if (!id || (!isEmail(id) && !isPhone(id))) {
      setError('Please enter a valid email or 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    try {
      const resp = await axios.post(`${config.API_BASE_URL}/associates/check`, { identifier: id });
      if (!resp.data?.registered) {
        setStep('alert');
        return;
      }

      const kind = resp.data.account_kind || 'associate';
      setAccountKind(kind);

      // Event Organizer / VSP signed up via B2B wizard — password already set; use agency/Valet login APIs (not User associates).
      if (kind === 'agency' || kind === 'valet_service') {
        setStep('password');
        return;
      }

      if (resp.data.first_login_required) {
        setPurpose('associate_login');
        await axios.post(`${config.API_BASE_URL}/associates/send-otp`, { identifier: id, purpose: 'associate_login' });
        resetOtp();
        setStep('otp');
        showSnackbar('OTP sent successfully', 'success');
      } else {
        setStep('password');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setIsLoading(true);
    try {
      await axios.post(`${config.API_BASE_URL}/associates/send-otp`, { identifier: identifier.trim(), purpose });
      showSnackbar('OTP resent successfully', 'success');
      resetOtp();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpConfirm = async () => {
    if (!otpValid) return;
    setError('');
    setIsLoading(true);
    try {
      if (purpose === 'associate_otp_login') {
        // Single call: verify OTP + get user+hotels (or agency/VS session)
        const resp = await axios.post(`${config.API_BASE_URL}/associates/otp-login`, {
          identifier: identifier.trim(),
          otp: otpValue,
          purpose,
        });
        const kind = resp.data.account_kind;
        if (kind === 'agency') {
          const agency = resp.data.agency;
          await persistentStorage.set('currentAgencyId', agency.agency_id);
          await persistentStorage.set('currentAgencyName', agency.name);
          await persistentStorage.set('currentRole', 'agency');
          await persistentStorage.set('currentAgency', JSON.stringify(agency));
          await markSessionStart();
          showSnackbar('Login successful!', 'success');
          navigate('/event-organizer/dashboard');
        } else if (kind === 'valet_service') {
          const vs = resp.data.valet_service;
          await persistentStorage.set('currentValetServiceId', vs.valet_service_id);
          await persistentStorage.set('currentValetServiceName', vs.name);
          await persistentStorage.set('currentRole', 'valet_service');
          await persistentStorage.set('currentValetService', JSON.stringify(vs));
          await markSessionStart();
          showSnackbar('Login successful!', 'success');
          navigate('/valet-service/dashboard');
        } else {
          await handlePasswordLoginResponse(resp);
        }
      } else {
        // First-login or forgot-password: verify OTP then go to set-password screen
        await axios.post(`${config.API_BASE_URL}/associates/verify-otp`, {
          identifier: identifier.trim(),
          otp: otpValue,
          purpose,
        });
        setStep('setPassword');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async () => {
    setError('');
    if (!newPassword || newPassword.length < 6) return setError('Password must be at least 6 characters');
    if (newPassword !== confirmNewPassword) return setError('Passwords do not match');

    setIsLoading(true);
    try {
      await axios.post(`${config.API_BASE_URL}/associates/set-password`, {
        identifier: identifier.trim(),
        new_password: newPassword,
        account_kind: accountKind,
      });

      // Use the correct login method based on account kind
      if (accountKind === 'agency') {
        await loginAgencySession(identifier.trim(), newPassword);
        return;
      }
      if (accountKind === 'valet_service') {
        await loginValetServiceSession(identifier.trim(), newPassword);
        return;
      }
      const resp = await axios.post(`${config.API_BASE_URL}/associates/password-login`, {
        identifier: identifier.trim(),
        password: newPassword,
      });
      await handlePasswordLoginResponse(resp);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not set password');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setError('');
    if (!password) return setError('Please enter password');
    const id = identifier.trim();
    setIsLoading(true);
    try {
      if (accountKind === 'agency') {
        await loginAgencySession(id, password);
        return;
      }
      if (accountKind === 'valet_service') {
        await loginValetServiceSession(id, password);
        return;
      }
      const resp = await axios.post(`${config.API_BASE_URL}/associates/password-login`, {
        identifier: id,
        password,
      });
      await handlePasswordLoginResponse(resp);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithOtp = async () => {
    setError('');
    setIsLoading(true);
    try {
      setPurpose('associate_otp_login');
      await axios.post(
        `${config.API_BASE_URL}/associates/send-otp`,
        { identifier: identifier.trim(), purpose: 'associate_otp_login' }
      );
      resetOtp();
      setStep('otp');
      showSnackbar(`OTP sent to your registered ${isEmail(identifier.trim()) ? 'email' : 'mobile'}`, 'success');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setIsLoading(true);
    try {
      setPurpose('associate_forgot_password');
      const res = await axios.post(
        `${config.API_BASE_URL}/associates/send-otp`,
        { identifier: identifier.trim(), purpose: 'associate_forgot_password' }
      );
      // Preserve account_kind returned by backend (agency / valet_service)
      if (res.data?.account_kind) setAccountKind(res.data.account_kind);
      resetOtp();
      setStep('otp');
      showSnackbar('OTP sent to your registered email', 'success');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && otpRefs.current[index + 1]) otpRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && otpRefs.current[index - 1]) {
      const next = [...otp];
      next[index - 1] = '';
      setOtp(next);
      otpRefs.current[index - 1].focus();
    }
  };

  // Clear stale errors only when the user actually types — listen to value
  // changes, not the error state itself. The previous version included `error`
  // in the dep array which caused setError(...) from any handler to be wiped
  // on the very next render whenever the form had values, hiding the message.
  const lastInputsRef = useRef({ identifier, password, otpValue, newPassword, confirmNewPassword });
  useEffect(() => {
    const prev = lastInputsRef.current;
    const changed =
      prev.identifier !== identifier ||
      prev.password !== password ||
      prev.otpValue !== otpValue ||
      prev.newPassword !== newPassword ||
      prev.confirmNewPassword !== confirmNewPassword;
    lastInputsRef.current = { identifier, password, otpValue, newPassword, confirmNewPassword };
    if (changed && error) setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, password, otpValue, newPassword, confirmNewPassword]);

  return (
    <div className={classes.rootContainer}>
      <div className={classes.loginCard}>
        <IconButton className={classes.topBack} onClick={goBack}>
          <ArrowBack />
        </IconButton>

        <img src={ParkingImage} alt="NimblePark Logo" className={classes.logo} />
        <div className={classes.tagline}>Where Every Minute Counts</div>

        <div className={classes.formContainer}>
          {step === 'identifier' && (
            <>
              <Typography sx={{ color: '#C084FC', fontSize: 30, fontFamily: 'Poppins', fontWeight: 700, mb: 0.5 }}>
                Hello!
              </Typography>
              <Typography sx={{ color: '#C084FC', fontSize: 30, fontFamily: 'Poppins', fontWeight: 400, mb: 2.5 }}>
                Associate Login
              </Typography>

              <TextField
                className={classes.phoneInput}
                fullWidth
                placeholder="Enter Email/Mobile Number"
                variant="outlined"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {identifier.includes('@') ? <Email /> : <Phone />}
                    </InputAdornment>
                  ),
                }}
              />

              <button className={classes.signInButton} onClick={handleNext} disabled={isLoading || !identifier} style={{ marginTop: 22 }}>
                {isLoading ? <CircularProgress size={20} className={classes.loadingSpinner} /> : 'Next'}
              </button>

              <div className={classes.linksContainer}>
                <div className={classes.signUpText}>
                  <span style={{ color: '#B6B6B6' }}>Don't have an account? </span>
                  <span
                    onClick={() => navigate('/signup-as')}
                    style={{ color: '#C084FC', cursor: 'pointer', fontWeight: 500 }}
                  >
                    Sign up
                  </span>
                </div>
                <Link to="/login" className={classes.customerLogin}>
                  Customer Login
                </Link>
              </div>
            </>
          )}

          {step === 'otp' && (
            <>
              <Typography sx={{ color: '#C084FC', fontSize: 22, fontFamily: 'Poppins', fontWeight: 700 }}>
                Verification Code
              </Typography>
              <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontFamily: 'Poppins', fontWeight: 500, mt: 1 }}>
                We have sent the verification code to your {isEmail(identifier) ? 'email' : 'mobile'}.
              </Typography>

              <div className={classes.otpRow}>
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (otpRefs.current[idx] = el)}
                    className={`${classes.otpBox} ${digit ? classes.otpBoxActive : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    disabled={isLoading}
                  />
                ))}
              </div>

              <div className={classes.resendOtp} onClick={isLoading ? undefined : handleResendOtp}>
                Resend OTP?
              </div>

              <button
                className={classes.signInButton}
                onClick={handleOtpConfirm}
                disabled={isLoading || !otpValid}
                style={{ marginTop: 22 }}
              >
                {isLoading ? <CircularProgress size={20} className={classes.loadingSpinner} /> : 'Confirm'}
              </button>
            </>
          )}

          {step === 'setPassword' && (
            <>
              <Typography sx={{ color: '#C084FC', fontSize: 22, fontFamily: 'Poppins', fontWeight: 700 }}>
                Set a New Password
              </Typography>
              <Typography sx={{ color: '#B6B6B6', fontSize: 13, fontFamily: 'Poppins', fontWeight: 500, mt: 1, mb: 2 }}>
                Set a strong and secure password to protect your account.
              </Typography>

              <TextField
                className={classes.passwordInput}
                fullWidth
                placeholder="Password"
                type={showNewPassword ? 'text' : 'password'}
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                style={{ marginBottom: 18 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword((p) => !p)} edge="end">
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                className={classes.passwordInput}
                fullWidth
                placeholder="Confirm Password"
                type={showConfirmNewPassword ? 'text' : 'password'}
                variant="outlined"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmNewPassword((p) => !p)} edge="end">
                        {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <button
                className={classes.signInButton}
                onClick={handleSetPassword}
                disabled={isLoading || !newPassword || !confirmNewPassword}
                style={{ marginTop: 22 }}
              >
                {isLoading ? <CircularProgress size={20} className={classes.loadingSpinner} /> : 'Create Password'}
              </button>
            </>
          )}

          {step === 'password' && (
            <>
              <Typography sx={{ color: '#C084FC', fontSize: 22, fontFamily: 'Poppins', fontWeight: 700 }}>
                Enter Your Password
              </Typography>
              <Typography sx={{ color: '#B6B6B6', fontSize: 13, fontFamily: 'Poppins', fontWeight: 500, mt: 1, mb: 2 }}>
                Enter the password to continue.
              </Typography>

              <TextField
                className={classes.passwordInput}
                fullWidth
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <button
                className={classes.signInButton}
                onClick={handlePasswordLogin}
                disabled={isLoading || !password}
                style={{ marginTop: 22 }}
              >
                {isLoading ? <CircularProgress size={20} className={classes.loadingSpinner} /> : 'Confirm Password'}
              </button>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                <span className={classes.forgotPassword} onClick={handleForgotPassword}>Forgot Password?</span>
                <span className={classes.forgotPassword} onClick={handleLoginWithOtp}>Login with OTP</span>
              </div>
            </>
          )}

          {step === 'alert' && (
            <>
              <div style={{ marginTop: 80, textAlign: 'center' }}>
                <div
                  style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 34,
                    fontWeight: 800,
                  }}
                >
                  !
                </div>
                <Typography sx={{ color: '#C084FC', fontFamily: 'Poppins', fontWeight: 700, mt: 2 }}>
                  ALERT
                </Typography>
                <Typography sx={{ color: '#B6B6B6', fontFamily: 'Poppins', fontWeight: 500, mt: 1, px: 1 }}>
                  You are not a registered associate, please signup to serve your property or events to serve with NimblePark.
                </Typography>
              </div>

              <button className={classes.signInButton} onClick={() => navigate('/signup-as')} style={{ marginTop: 48 }}>
                Sign Up Now
              </button>
            </>
          )}

          {error ? <Typography className={classes.errorText}>{error}</Typography> : null}
        </div>
      </div>
    </div>
  );
}

